/**
 * Export map — spec §F5.10 group 3, the modal behind `#btnExportMap`.
 *
 *   Format  [PNG 300 dpi | PDF layout | GeoTIFF visible rasters | GeoPackage vectors |
 *            CSV current view]
 *   Extent  [current view | programme | selected unit | drawn AOI]
 *
 * **CSV is an ordinary link, everything else is a job.** §E8 says "CSV: immediate (no
 * job)", so that option renders an `<a href download>` pointing at `/indicators/export`
 * with the map's own scope on it (`?indicator=&level=&year=&country=&parent=`) — the
 * browser saves it, which means it survives a slow server, appears in the download manager
 * and needs no blob, no object URL and nothing to revoke. The other four POST to
 * `/export/map`, toast, and poll the job.
 *
 * **CSV is also the one format the Extent select cannot fully serve.** That route takes a
 * level, a scope and a unit id, and has nowhere to put a viewport box or a drawn ring, so
 * "Current view" and "Drawn area (AOI)" are disabled for it (`CSV_EXTENTS`) rather than
 * accepted and silently answered with the whole level.
 *
 * **The modal stays open while the job runs**, and that is a deliberate departure from
 * §F5.10's "toast + notification on completion". A toast is a 3.4 s text pill (§F2) with
 * nowhere to put a link, and a notification centre does not exist yet; a modal that closed
 * on submit would leave the finished file reachable only from a toast that had already
 * gone. So the panel keeps the progress bar and then shows the Download button, and the
 * toast is the thing that says it is ready. When §F6's dock lands (Task 10) it brings a
 * poll hook and a place for a run history, and this can move behind it.
 *
 * **What the reader may not have is said before they press, and again after.** Before: a
 * data format (`DOWNLOADABLE`) carries `export.downloadNote`, which says that layers this
 * role may only *view* are left out of it — the rule, while the select is still open. After:
 * the server decides which switched-on layers this role may actually put in this format (§I3:
 * a picture needs LIMITED, a GeoTIFF or a GeoPackage needs FULL) and answers with `withheld`,
 * and the count of the ones refused for the role is what the panel prints. The **count** is
 * only ever the server's — the panel holds `layersOn` but not the catalogue those ids' `access`
 * lives in, so a number computed here would be a guess dressed as a fact.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  fetchAnalysisJob,
  indicatorsExportUrl,
  submitExportMap,
  type ExportExtent,
  type ExportFormat,
  type ExportResult,
  type ExportState,
  type ExportWithheld,
  type FeatureBbox,
} from '../../lib/api';
import { aoiRing } from '../../lib/analysis';
import { toast } from '../../lib/toast';
import { selectScopeParent, selectYear, useAppStore } from '../../lib/state';
import { Widget } from '../../components/Widget';
import {
  CSV_EXTENTS,
  DOWNLOADABLE,
  EXTENTS,
  FORMATS,
  nextDelay,
  POLL_START_MS,
  POLL_TIMEOUT_MS,
} from './exportOptions';
import { useMapOrNull } from './mapContext';

export interface ExportModalProps {
  onClose: () => void;
}

type Phase = 'idle' | 'running' | 'done' | 'error';

export function ExportModal({ onClose }: ExportModalProps) {
  const { t } = useTranslation();
  // `useMapOrNull` rather than `useMap`: the panel is testable on its own, and "no map
  // yet" is a real state on a cold load — `extent: view` is what needs the bounds, and it
  // refuses with a sentence rather than throwing out of a render.
  const map = useMapOrNull();

  const [format, setFormat] = useState<ExportFormat>('png');
  const [extent, setExtent] = useState<ExportExtent>('view');
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ExportResult | null>(null);
  const [withheld, setWithheld] = useState<ExportWithheld[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const indicator = useAppStore((s) => s.indicator);
  const level = useAppStore((s) => s.reportingLevel);
  const lastAdminLevel = useAppStore((s) => s.lastAdminLevel);
  const year = useAppStore(selectYear);
  const country = useAppStore((s) => s.country);
  const parent = useAppStore(selectScopeParent);
  const layersOn = useAppStore((s) => s.layersOn);
  const overlays = useAppStore((s) => s.overlays);
  const biv = useAppStore((s) => s.biv);
  const indStyle = useAppStore((s) => s.indStyle);
  const cumulative = useAppStore((s) => s.cumulative);
  const aoi = useAppStore((s) => s.aoi);
  const selectedUnit = useAppStore((s) => s.selectedUnit);

  /** Stopping the poll when the panel closes — a timer outliving its component is a leak. */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  /**
   * §F5.10 disables an extent that has nothing behind it, with the reason in the tooltip —
   * and an extent the chosen *format* cannot honour is exactly the same case. CSV is a row
   * query with no bbox and no polygon (`CSV_EXTENTS`), and an enabled "Current view" there
   * downloaded the whole level while looking like it had worked.
   */
  const disabled: Partial<Record<ExportExtent, string>> = {
    ...(aoi ? {} : { aoi: t('export.noAoi') }),
    ...(selectedUnit ? {} : { unit: t('export.noUnit') }),
    ...(format === 'csv'
      ? Object.fromEntries(
          EXTENTS.filter((key) => !CSV_EXTENTS.includes(key)).map((key) => [
            key,
            t('export.csvNoExtent'),
          ]),
        )
      : {}),
  };

  /** Switching format must never leave the Extent select sitting on a disabled option. */
  const changeFormat = (next: ExportFormat) => {
    setFormat(next);
    if (next === 'csv' && !CSV_EXTENTS.includes(extent)) setExtent('programme');
  };

  const state: ExportState = {
    indicator,
    level,
    year,
    country,
    scope: parent,
    layersOn: [...layersOn],
    overlays: overlays.map((o) => ({ id: o.id, color: o.color })),
    biv: biv.on,
    ramp: indStyle.ramp,
    method: indStyle.method,
    k: indStyle.k,
    // `[lon, lat]`, not the store's `[lat, lon]` — see `aoiRing` and NOTES #630. Sending
    // the pair the other way round put a Burkinabè AOI in the Gulf of Guinea, and the
    // export still succeeded: a 70 kB picture of empty ocean, with no error anywhere.
    aoi: aoi ? aoiRing(aoi.pts) : null,
    unit_id: selectedUnit,
    cumulative,
  };

  /**
   * `/indicators/export` enumerates **admin** units, so a map in §M3.5's watershed or
   * partnership mode has no level to ask for. The reader's last admin level is the honest
   * substitution — it is the level the map falls back to anyway when the overlay hierarchy
   * stops applying — and the panel says so rather than silently exporting a different set.
   */
  const csvLevel = typeof level === 'number' ? level : lastAdminLevel;
  const csvHref = indicatorsExportUrl({
    indicator,
    unitId: extent === 'unit' ? selectedUnit : null,
    level: csvLevel,
    year,
    country,
    parent,
  });

  const poll = useCallback(
    (jobId: string, delay: number, deadline: number) => {
      timer.current = setTimeout(() => {
        void fetchAnalysisJob<ExportResult>(jobId)
          .then((job) => {
            if (!alive.current) return;
            setProgress(job.progress ?? 0);
            if (job.status === 'done') {
              setPhase('done');
              setResult(job.result ?? null);
              setWithheld(job.result?.withheld ?? null);
              toast(t('export.ready'));
              return;
            }
            if (job.status === 'error') {
              setPhase('error');
              setMessage(job.error ?? t('export.failed'));
              toast(job.error ?? t('export.failed'));
              return;
            }
            if (Date.now() > deadline) {
              setPhase('error');
              setMessage(t('export.timedOut'));
              return;
            }
            poll(jobId, nextDelay(delay), deadline);
          })
          .catch((err: unknown) => {
            if (!alive.current) return;
            setPhase('error');
            setMessage(err instanceof Error ? err.message : t('export.failed'));
          });
      }, delay);
    },
    [t],
  );

  const submit = () => {
    if (format === 'csv') return; // the CSV option is a link, not a button
    const bounds = map?.getBounds();
    if (extent === 'view' && !bounds) {
      toast(t('export.noView'));
      return;
    }
    const bbox: FeatureBbox | undefined =
      extent === 'view' && bounds
        ? [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]
        : undefined;

    setPhase('running');
    setProgress(0);
    setResult(null);
    setMessage(null);
    void submitExportMap({ format, extent, ...(bbox ? { bbox } : {}), state })
      .then((submission) => {
        if (!alive.current) return;
        setWithheld(submission.withheld ?? null);
        // A §E7 cache hit answers with the file already written — no poll, no wait.
        if (submission.cached && submission.result) {
          setPhase('done');
          setResult(submission.result);
          setProgress(100);
          toast(t('export.ready'));
          return;
        }
        if (submission.status === 'error') {
          setPhase('error');
          setMessage(submission.error ?? t('export.failed'));
          return;
        }
        toast(t('export.queued'));
        poll(submission.job_id, POLL_START_MS, Date.now() + POLL_TIMEOUT_MS);
      })
      .catch((err: unknown) => {
        if (!alive.current) return;
        setPhase('error');
        setMessage(err instanceof Error ? err.message : t('export.failed'));
      });
  };

  const omitted = (withheld ?? []).filter((w) => w.reason === 'access');
  const running = phase === 'running';

  return (
    <Widget
      id="exportModal"
      title={t('export.title')}
      icon="download"
      collapsible={false}
      onClose={onClose}
      className="exp-widget"
    >
      <div
        className="exp-body"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
          }
        }}
      >
      <label className="fld" htmlFor="expFormat">
        {t('export.format')}
      </label>
      <select
        id="expFormat"
        className="std"
        value={format}
        disabled={running}
        onChange={(e) => changeFormat(e.target.value as ExportFormat)}
      >
        {FORMATS.map((key) => (
          <option key={key} value={key}>
            {t(`export.fmt.${key}`)}
          </option>
        ))}
      </select>

      <label className="fld" htmlFor="expExtent">
        {t('export.extent')}
      </label>
      <select
        id="expExtent"
        className="std"
        value={extent}
        disabled={running}
        onChange={(e) => setExtent(e.target.value as ExportExtent)}
      >
        {EXTENTS.map((key) => (
          <option key={key} value={key} disabled={Boolean(disabled[key])} title={disabled[key]}>
            {t(`export.ext.${key}`)}
          </option>
        ))}
      </select>

      <p className="exp-note" data-note="scope">
        {format === 'csv' ? t('export.csvNote') : t('export.jobNote')}
      </p>
      {format !== 'csv' && !DOWNLOADABLE.includes(format) ? (
        <p className="exp-note" data-note="basemap">
          {t('export.noBasemap')}
        </p>
      ) : null}
      {format === 'csv' && typeof level !== 'number' ? (
        <p className="exp-note" data-note="csvlevel">
          {t('export.csvAdminLevel', { level: csvLevel })}
        </p>
      ) : null}
      {DOWNLOADABLE.includes(format) ? (
        <p className="exp-note" data-note="downloadable">
          {t('export.downloadNote')}
        </p>
      ) : null}

      {running ? (
        <div className="exp-progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${Math.max(4, progress)}%` }} />
        </div>
      ) : null}

      {omitted.length ? (
        <p className="exp-note warn" data-note="omitted">
          {t('export.omitted', { count: omitted.length, layers: omitted.map((w) => w.layer).join(', ') })}
        </p>
      ) : null}
      {message ? (
        <p className="exp-note warn" data-note="error">
          {message}
        </p>
      ) : null}

      <div className="exp-actions">
        <button type="button" className="btn ghost sm" onClick={onClose}>
          {t('widget.close')}
        </button>
        {phase === 'done' && result ? (
          <a className="btn primary sm" href={result.artifact_uri} download data-role="download">
            {t('export.download', { ext: result.ext.toUpperCase() })}
          </a>
        ) : format === 'csv' ? (
          <a
            className="btn primary sm"
            href={csvHref}
            download
            data-role="csv"
            onClick={() => toast(t('export.csvStarted'))}
          >
            {t('export.run')}
          </a>
        ) : (
          <button type="button" className="btn primary sm" disabled={running} onClick={submit}>
            {running ? t('export.working') : t('export.run')}
          </button>
        )}
      </div>
      </div>
    </Widget>
  );
}

export default ExportModal;
